/**
 * Splits a line Milo wrote into pieces the conversation can draw as links.
 *
 * Milo answers a guest's "how do I get to the castle?" with a Google Maps address
 * (docs/AI.md §24.3, `maps_link`), and until this file existed that address was TEXT:
 * a guest standing on a pavement read forty characters of URL and had no way to follow
 * it but to select them with a thumb.
 *
 * # Smaller than the desk's own, on purpose
 *
 * `pms-web/src/libs/linkify.ts` splits four kinds — external, mail, an internal route
 * into that portal, and a bare e-mail address. This one splits ONE: an `https://`
 * address. The reasons are the guest's and not the receptionist's:
 *
 *   - **No internal routes.** The desk's splitter offers a path like `/rezervacije/42`
 *     because the model there writes about screens the receptionist can open. Nothing
 *     Milo tells a guest is a route in this portal, and a link he invented into it
 *     would be a guest sent to a blank screen by their own hotel.
 *   - **No `http://` and no `mailto:`.** This portal is opened on a telephone on a
 *     strange network. A plain-text link is a link somebody can sit in the middle of,
 *     and the hotel's e-mail address belongs on the Hiša screen, where the house wrote
 *     it, rather than in a sentence a model assembled.
 *
 * # Two shapes, one segment
 *
 * Milo writes an address in two shapes now. The bare one is what this file was written
 * for; the other is Markdown — `[Pot do hotela z avtom](https://www.google.com/maps/…)`
 * — and until it was recognised the guest read the brackets and forty characters of
 * address INSIDE them, which is the very thing the bare scanner exists to prevent. A
 * Markdown link produces ONE link segment: the label is what is drawn, the address is
 * where it goes, and the whole `[…](…)` is what it stands in for.
 *
 * The label is drawn WHOLE and never shortened. `shortenUrl` cuts an address because
 * forty characters of query string are not worth a line of a bubble; a label is prose
 * the model wrote for this reader, and cutting it cuts a sentence.
 *
 * Nothing else in Markdown is understood — `**bold**` is not a link and is left exactly
 * as it was written. This file splits addresses; it is not a Markdown renderer, and the
 * day it becomes one is the day something here starts producing markup.
 *
 * # Data, never markup
 *
 * Nothing here produces HTML and nothing here is ever handed to `v-html`. It returns a
 * list of SEGMENTS and the template decides what each one is drawn with, so every
 * character still goes through `{{ }}` and is escaped. The text being split is written
 * by a language model, which makes it exactly the input that must never be able to
 * bring an element with it.
 *
 * # Worked examples — there is no test runner in this portal
 *
 *   1. `"Pot do hotela: https://www.google.com/maps/dir/?api=1&destination=X."`
 *      → text, link, text `"."` — the full stop ends the sentence, not the address.
 *   2. `"(https://a.example/x)"` → text `"("`, link, text `")"`, but an address that
 *      opened a bracket itself keeps the one that closes it.
 *   3. `"pokličite 01 234 5678"` → one text segment; a telephone number is not a link.
 *   4. A long address is shortened for READING and never for following: the label is
 *      cut, `url` is whole.
 *
 * And the Markdown cases, which is the table this branch was written against. A segment
 * is written `kind(text → url)`:
 *
 *   | line                                      | segments                               |
 *   | ----------------------------------------- | -------------------------------------- |
 *   | `Pot: [Pot do hotela](https://m.example)` | text `"Pot: "`, link(`Pot do hotela` → |
 *   |                                           | `https://m.example`)                   |
 *   | `[Grad Ptuj – pot](https://a.example)`    | one link, the label whole, diacritics  |
 *   |                                           | and dash included                      |
 *   | `[Pot](https://x.example/?a=1&b=%C5%A1)`  | one link; `&` and `%C5%A1` are address |
 *   |                                           | characters and end nothing             |
 *   | `[Hotel](https://en.wikipedia.org/wiki/`  | one link, the inner `()` kept — the    |
 *   | `Hotel_(disambiguation))`                 | LAST `)` closes the Markdown link      |
 *   | `[Pot](https://a.example) in https://b.`  | link, text `" in "`, link — the bare   |
 *   | `example`                                 | address after it is split as always    |
 *   | `[Pot](http://a.example)`                 | all text: `http://` is not a link in   |
 *   |                                           | this portal, in either shape           |
 *   | `[Pot](https://a.example`                 | text `"[Pot]("`, then the BARE address |
 *   |                                           | — an unclosed Markdown link is not one |
 *   | `Nekaj [ tukaj in [Pot](https://a.`       | text `"Nekaj [ tukaj in "`, link(`Pot` |
 *   | `example)`                                | → …) — a stray `[` loses to the second |
 */

/** What one piece of a split line is. */
export type LinkKind = 'text' | 'link'

export interface ILinkSegment {
    kind: LinkKind
    /** What is drawn. A long address is shortened; text is whole. */
    text: string
    /** Where it goes. Empty on a `text` segment. */
    url: string
}

/**
 * How long an address may be before it is shortened, measured with the scheme taken
 * off. A bubble in this portal is 86% of a telephone, which is about this many
 * characters before it wraps twice.
 */
export const MAX_LINK_LABEL = 38

/**
 * A Markdown link, `[label](https://…)`.
 *
 * The label is FENCED by three things and not one: it holds no `]`, which is what ends
 * it; no `[` either; and no line break, in at most a hundred and twenty characters.
 *
 * Barring `[` is the half that earns its place. Without it a stray bracket earlier in a
 * sentence reaches forward to the first `](https://` it can find and swallows the prose
 * in between — "Nekaj [ tukaj in [Pot](https://…)" drew ONE link labelled " tukaj in
 * [Pot". With it, the link starts at the second bracket and the prose stays prose. The
 * length is the backstop for the line that has no stray bracket at all: a label is a
 * phrase somebody reads in one bubble, and a longer one is a paragraph the model did
 * not mean as a label.
 *
 * The address is `https://` for the same reason the bare scanner is, and it may carry a
 * bracket PAIR of its own — `…/Hotel_(disambiguation)` is a real address — while a lone
 * `)` ends it. That is the same rule `trimTrailing` applies below, written as a pattern
 * instead of as a walk backwards, because here the `)` that ends the link is a
 * character the syntax owns rather than one the sentence lent it.
 *
 * Its two groups are the ONLY capturing groups in `PATTERN`; the loop reads them to
 * tell a Markdown match from a bare one.
 */
const MARKDOWN = String.raw`\[([^\]\[\r\n]{1,120})\]\((https:\/\/(?:[^\s()<>"']|\([^\s()<>"']*\))+)\)`

/**
 * The two shapes, and the order between them decides nothing: a Markdown link starts at
 * its `[`, which is earlier in the line than the `https://` inside it, and `exec` takes
 * the earliest match wherever both could apply.
 *
 * `https://` and nothing else — see the note above about why not `http://`.
 */
const PATTERN = [MARKDOWN, String.raw`https:\/\/[^\s<>"']+`].join('|')

const SENTENCE_END = /[.,;:!?]+$/

/**
 * Takes the sentence's own punctuation back off the end of an address.
 *
 * The closing bracket is the one that cannot be stripped blindly: it ends an aside far
 * more often than it ends a URL, but a URL that opened a bracket itself owns the one
 * that closes it.
 */
const trimTrailing = (value: string): string => {
    let out = value.replace(SENTENCE_END, '')

    while (out.endsWith(')') && !out.includes('(')) out = out.slice(0, -1).replace(SENTENCE_END, '')

    return out
}

/**
 * An address as it is READ: no scheme, no `www.`, no trailing slash, and cut with an
 * ellipsis when it is longer than a bubble. What it POINTS at is untouched — the two
 * are separate fields precisely so that shortening can never shorten a link.
 */
export const shortenUrl = (url: string): string => {
    const bare = url
        .replace(/^https:\/\//i, '')
        .replace(/^www\./i, '')
        .replace(/\/$/, '')

    return bare.length > MAX_LINK_LABEL ? `${bare.slice(0, MAX_LINK_LABEL - 1)}…` : bare
}

/**
 * The line, split.
 *
 * Joining every segment's source back together reproduces the input exactly: nothing
 * is dropped and nothing is invented, which is what makes it safe to draw.
 */
export const linkify = (text: string): ILinkSegment[] => {
    const segments: ILinkSegment[] = []

    if (!text) return segments

    // Built here rather than once at module scope: a /g regex carries `lastIndex`
    // between calls, and a shared one would start the second line where it left the
    // first.
    const pattern = new RegExp(PATTERN, 'g')

    let last = 0
    let match: RegExpExecArray | null

    while ((match = pattern.exec(text)) !== null) {
        const whole = match[0]
        const [, markdownLabel, markdownUrl] = match

        // A Markdown link, consumed whole: the source span is the entire `[…](…)` and
        // exactly one segment comes out of it. Nothing is trimmed off the end — the `)`
        // there belongs to the link's own syntax, not to the sentence around it.
        if (markdownLabel !== undefined && markdownUrl !== undefined) {
            if (match.index > last) segments.push({ kind: 'text', text: text.slice(last, match.index), url: '' })

            segments.push({ kind: 'link', text: markdownLabel, url: markdownUrl })

            last = match.index + whole.length
            pattern.lastIndex = last

            continue
        }

        const token = trimTrailing(whole)
        const label = shortenUrl(token)

        if (!label) {
            // "https://" with the address trimmed off it is not an address. Left where
            // it is; it becomes part of the next text segment.
            pattern.lastIndex = match.index + whole.length

            continue
        }

        if (match.index > last) segments.push({ kind: 'text', text: text.slice(last, match.index), url: '' })

        segments.push({ kind: 'link', text: label, url: token })

        last = match.index + token.length
        pattern.lastIndex = last
    }

    if (last < text.length) segments.push({ kind: 'text', text: text.slice(last), url: '' })

    return segments
}
